import { shipmentHost, toHostedShipmentUrl } from './admin';

// CRM lives in api-shipments (freshest order/revenue truth). All endpoints are
// backend-enforced by CRM access middleware; the dashboard nav is additionally
// gated by the /crm/access probe.
const base = shipmentHost + '/crm';

// Wrap so local-debug hosts are redirected to the hosted backend like other
// api-shipments admin calls.
const url = path => toHostedShipmentUrl(base + path);

export const crmAccess = url('/access');
export const crmMeta = url('/meta');

// In-dashboard merchant help assistant ("Bill").
export const merchantChatEnabled = url('/chat/merchant/enabled');
export const merchantChatStart = url('/chat/merchant/start');
export const merchantChatMessage = url('/chat/merchant/message');
export const merchantChatPoll = url('/chat/merchant/poll');

export const crmUsers = url('/users');
export const crmDispatchAlertRecipients = url('/dispatch-alert-recipients');
export const crmDispatchAlertRecipient = id => url('/dispatch-alert-recipients/' + encodeURIComponent(id));
export const crmUser = id => url('/users/' + encodeURIComponent(id));

export const crmPipeline = url('/pipeline');

export const crmVisitRoutes = url('/visit-routes');
export const crmVisitRouteAssignees = url('/visit-routes/assignees');
export const crmVisitRoute = id => url('/visit-routes/' + encodeURIComponent(id));
export const crmVisitRouteContact = (id, stopId) =>
  url('/visit-routes/' + encodeURIComponent(id) + '/stops/' + encodeURIComponent(stopId) + '/contact');

export const crmCompanies = url('/companies');
export const crmCompanyBoard = url('/companies/board');
export const crmCompany = id => url('/companies/' + encodeURIComponent(id));
export const crmCompanyStage = id => url('/companies/' + encodeURIComponent(id) + '/stage');
export const crmCompanyLost = id => url('/companies/' + encodeURIComponent(id) + '/lost');
export const crmCompanyRecompute = id => url('/companies/' + encodeURIComponent(id) + '/recompute');
export const crmCompanyActivities = id => url('/companies/' + encodeURIComponent(id) + '/activities');

export const crmContacts = url('/contacts');
export const crmContact = id => url('/contacts/' + encodeURIComponent(id));
export const crmContactEmail = id => url('/contacts/' + encodeURIComponent(id) + '/email');
export const crmContactEmailPreview = id => url('/contacts/' + encodeURIComponent(id) + '/email-preview');

export const crmCallList = url('/call-list');
export const crmTasks = url('/tasks');
export const crmTask = id => url('/tasks/' + encodeURIComponent(id));
export const crmCompanyCallOutcome = id => url('/companies/' + encodeURIComponent(id) + '/call-outcome');
export const crmReports = url('/reports');

export const crmAbandonedQuotes = url('/abandoned-quotes');
export const crmAbandonedQuotesDetect = url('/abandoned-quotes/detect');
export const crmAbandonedQuote = id => url('/abandoned-quotes/' + encodeURIComponent(id));
export const crmAbandonedQuoteOutreach = id => url('/abandoned-quotes/' + encodeURIComponent(id) + '/outreach');
export const crmAbandonedQuoteOutreachPreview = id => url('/abandoned-quotes/' + encodeURIComponent(id) + '/outreach-preview');
export const crmAbandonedQuoteDetail = id => url('/abandoned-quotes/' + encodeURIComponent(id) + '/quote');

export const crmNotifications = url('/notifications');
export const crmNotificationRead = id => url('/notifications/' + encodeURIComponent(id) + '/read');
export const crmNotificationsReadAll = url('/notifications/read-all');

export const crmProspectingMeta = url('/prospecting/meta');
export const crmProspectingSearch = url('/prospecting/search');
export const crmProspectingSearches = url('/prospecting/searches');
export const crmProspectingSearchResults = id => url('/prospecting/searches/' + encodeURIComponent(id));
export const crmProspectingAdd = url('/prospecting/add');

export const crmChatConversations = url('/chat/conversations');
export const crmChatConversation = id => url('/chat/conversations/' + encodeURIComponent(id));
export const crmChatSend = id => url('/chat/conversations/' + encodeURIComponent(id) + '/message');
export const crmChatAssign = id => url('/chat/conversations/' + encodeURIComponent(id) + '/assign');
export const crmChatClose = id => url('/chat/conversations/' + encodeURIComponent(id) + '/close');
export const crmChatReopen = id => url('/chat/conversations/' + encodeURIComponent(id) + '/reopen');
export const crmChatUnread = url('/chat/unread');

export const crmVisitors = url('/visitors');
export const crmVisitorsHotCount = url('/visitors/hot-count');
export const crmVisitorsMarkReviewed = url('/visitors/mark-reviewed');
export const crmVisitor = id => url('/visitors/' + encodeURIComponent(id));
export const crmVisitorAddLead = id => url('/visitors/' + encodeURIComponent(id) + '/add-lead');
export const crmVisitorIgnore = id => url('/visitors/' + encodeURIComponent(id) + '/ignore');

export const crmCompanyClassify = id => url('/companies/' + encodeURIComponent(id) + '/classify');
export const crmClassifyRun = url('/classify/run');
export const crmImport = url('/import');

export const crmTemplates = url('/templates');
export const crmTemplate = id => url('/templates/' + encodeURIComponent(id));
export const crmTemplatePreview = url('/templates/preview');
export const crmUploadAsset = url('/upload-asset');

export const crmCampaigns = url('/campaigns');
export const crmCampaign = id => url('/campaigns/' + encodeURIComponent(id));
export const crmCampaignPreview = id => url('/campaigns/' + encodeURIComponent(id) + '/preview-audience');
export const crmCampaignSend = id => url('/campaigns/' + encodeURIComponent(id) + '/send');
export const crmCampaignStats = (id, params) => {
  const base = url('/campaigns/' + encodeURIComponent(id) + '/stats');
  const q = [];
  if (params && params.dateFrom) q.push('dateFrom=' + encodeURIComponent(params.dateFrom));
  if (params && params.dateTo) q.push('dateTo=' + encodeURIComponent(params.dateTo));
  return q.length ? base + (base.includes('?') ? '&' : '?') + q.join('&') : base;
};

export const crmAiDraft = url('/ai/draft-campaign');
export const crmAiMetrics = url('/ai/metrics');

export const crmPipelineBoard = pipeline => url('/pipelines/' + encodeURIComponent(pipeline) + '/board');
export const crmPipelineRecords = pipeline => url('/pipelines/' + encodeURIComponent(pipeline) + '/records');
export const crmPipelineRecord = (pipeline, id) =>
  url('/pipelines/' + encodeURIComponent(pipeline) + '/records/' + encodeURIComponent(id));
export const crmSequencesRun = url('/sequences/run');

export const crmMigrateMerchants = url('/migrate/merchants');

// Social media module (content scheduler + click attribution). Mounted at
// /crm/social on api-shipments.
const socialBase = shipmentHost + '/crm/social';
const socialUrl = path => toHostedShipmentUrl(socialBase + path);
export const crmSocialMeta = socialUrl('/meta');
export const crmSocialAccounts = socialUrl('/accounts');
export const crmSocialAccount = id => socialUrl('/accounts/' + encodeURIComponent(id));
export const crmSocialPosts = socialUrl('/posts');
export const crmSocialPost = id => socialUrl('/posts/' + encodeURIComponent(id));
export const crmSocialPostSchedule = id => socialUrl('/posts/' + encodeURIComponent(id) + '/schedule');
export const crmSocialPostPublish = id => socialUrl('/posts/' + encodeURIComponent(id) + '/publish');
export const crmSocialAnalytics = socialUrl('/analytics');
export const crmSocialFacebookOAuthStart = socialUrl('/oauth/facebook/start');
export const crmSocialFacebookTokenConnect = socialUrl('/oauth/facebook/token');
export const crmSocialGenerateContent = socialUrl('/generate/content');
export const crmSocialGenerateImage = socialUrl('/generate/image');
export const crmSocialGenerateStoryboard = socialUrl('/generate/storyboard');
export const crmSocialGenerateVideo = socialUrl('/generate/video');
export const crmSocialGenerateVideoStatus = jobId => socialUrl('/generate/video/' + encodeURIComponent(jobId));
export const crmSocialAutogen = socialUrl('/generate/autogen');

// SEO / Content module (AI articles + landing pages, human approval, WordPress
// publish). Mounted at /crm/seo on api-shipments.
const seoBase = shipmentHost + '/crm/seo';
const seoUrl = path => toHostedShipmentUrl(seoBase + path);
export const crmSeoMeta = seoUrl('/meta');
export const crmSeoArticles = seoUrl('/articles');
export const crmSeoArticle = id => seoUrl('/articles/' + encodeURIComponent(id));
export const crmSeoArticleApprove = id => seoUrl('/articles/' + encodeURIComponent(id) + '/approve');
export const crmSeoArticlePublish = id => seoUrl('/articles/' + encodeURIComponent(id) + '/publish');
export const crmSeoArticleImage = id => seoUrl('/articles/' + encodeURIComponent(id) + '/image');
export const crmSeoGenerate = seoUrl('/generate');
export const crmSeoKeywordsSuggest = seoUrl('/keywords/suggest');

// Local-only preview: lets the CRM workspace render on the local dev server
// (localhost) even before the api-shipments CRM backend is deployed, so the
// layout can be reviewed. NEVER true on a deployed host (hostname check), so
// production remains fully backend-gated. Opt out with REACT_APP_CRM_LOCAL_PREVIEW=false.
export const isCrmLocalPreview = () => {
  try {
    const host = typeof window !== 'undefined' && window.location ? window.location.hostname : '';
    const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
    return isLocalHost && process.env.REACT_APP_CRM_LOCAL_PREVIEW !== 'false';
  } catch (err) {
    return false;
  }
};
